import moment from "moment";
import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAppSelector } from "../../../../redux/store";
import {
  useLazyNumeroCarteQuery,
  useRetraitsQuery,
  useUpdateStatutMutation,
} from "../../../../utils/api/retrait/retrait.api";
import {
  destinationRetrait,
  origineVersement,
  Retrait,
  RETRAIT_STATUT,
  RetraitStatut,
  transitionsAdmin,
} from "../../../../utils/api/retrait/retrait.type";
import { COLORS } from "../../../../utils/constants";
import { formatMontant } from "../../../../utils/utils";
import SideBarAdmin from "../Layouts/SideBarAdmin/SideBarAdmin";
import TopBar from "../Layouts/TopBar/TopBar";

// Ces boutons ne s'affichent que sur le circuit manuel : l'admin paie
// lui-meme, puis consigne ce qu'il a fait. Les libelles le disent.
const ACTIONS: Record<string, string> = {
  approuve: "Approuver",
  refuse: "Refuser",
  en_cours: "Paiement en cours",
  verse: "Marquer comme versé",
  echoue: "Marquer en échec",
};


const nomProjet = (r: Retrait) =>
  r.projet_nom ||
  (typeof r.projet === "object" && r.projet ? r.projet.name : `#${r.projet}`);

const nomDemandeur = (r: Retrait) => r.demande_par_nom || "—";

// Vue globale : toutes les demandes de retrait, tous projets confondus.
// Le backend filtre deja selon le role, cet ecran n'ajoute qu'un filtre de statut.
function RetraitsAdmin() {
  const user = useAppSelector((s) => s.user.user);
  const isAdmin = user?.user_type === "admin";
  const [statut, setStatut] = useState("");
  const { data: retraits = [], isLoading } = useRetraitsQuery({
    statut: statut || undefined,
  });
  const [updateStatut, { isLoading: isUpdating }] = useUpdateStatutMutation();
  const [lireNumeroCarte, { isFetching: lectureCarte }] =
    useLazyNumeroCarteQuery();

  // Le numero n'est dechiffre qu'a la demande, au moment de verser. Le
  // titulaire et la banque sont saisis par le porteur : echappes avant
  // d'entrer dans le HTML de la fenetre.
  const afficherCarte = async (retrait: Retrait) => {
    const res: any = await lireNumeroCarte(retrait.slug, false);
    if (res?.error) {
      return Swal.fire({
        icon: "error",
        title: "Numéro indisponible",
        text: res.error?.data?.message || "Impossible de lire le numéro.",
        confirmButtonColor: COLORS.blue,
      });
    }
    const { carte_numero, titulaire, banque } = res.data;
    const echapper = (t: string | null) =>
      (t || "—").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const groupes = carte_numero.replace(/(\d{4})(?=\d)/g, "$1 ");
    const choix = await Swal.fire({
      title: "Numéro de carte",
      html: `<div style="font-family:monospace;font-size:22px;letter-spacing:2px;margin:8px 0">${groupes}</div>
             <div style="font-size:13px;color:rgba(0,0,0,.6)">${echapper(titulaire)} · ${echapper(banque)}</div>
             <div style="font-size:11px;color:rgba(0,0,0,.45);margin-top:10px">Effacé automatiquement une fois le retrait versé ou refusé.</div>`,
      showCancelButton: true,
      confirmButtonText: "Copier le numéro",
      cancelButtonText: "Fermer",
      confirmButtonColor: COLORS.blue,
    });
    if (choix.isConfirmed) {
      try {
        await navigator.clipboard.writeText(carte_numero);
      } catch {
        // Presse-papiers refuse (page non securisee) : le numero reste lisible.
      }
    }
  };

  const totaux = useMemo(() => {
    const somme = (f: (r: Retrait) => boolean) =>
      retraits.filter(f).reduce((acc, r) => acc + parseFloat(r.montant || "0"), 0);
    return {
      attente: somme((r) => r.statut === "demande"),
      encours: somme((r) => ["approuve", "en_cours"].includes(r.statut)),
      verse: somme((r) => r.statut === "verse"),
    };
  }, [retraits]);

  const changer = async (retrait: Retrait, nouveau: RetraitStatut) => {
    let motif: string | undefined;
    if (nouveau === "refuse" || nouveau === "echoue") {
      const { value, isDismissed } = await Swal.fire({
        title: ACTIONS[nouveau],
        input: "textarea",
        inputLabel: "Motif (communiqué au porteur du projet)",
        inputPlaceholder: "Expliquez la raison…",
        showCancelButton: true,
        cancelButtonText: "Annuler",
        confirmButtonText: "Valider",
        confirmButtonColor: COLORS.green,
      });
      if (isDismissed) return;
      motif = value || undefined;
    }
    const res: any = await updateStatut({
      slug: retrait.slug,
      statut: nouveau,
      motif,
    });
    if (res?.error) {
      Swal.fire({
        icon: "error",
        title: "Action impossible",
        text: res.error?.data?.message || "Veuillez réessayer.",
        confirmButtonColor: COLORS.green,
      });
    }
  };

  return (
    <div className="parametre-page">
      <TopBar index={11} />
      <SideBarAdmin index={11} />
      <div className="parametre-component m-tableau-bord pt-3 flex-r">
        <div className="admin-page-container">
          <div className="p4 p-xxl-3">
            <div className="row">
              <div className="col-md-12 maajjal-table-card px-3 py-4 ml-2 mr-3">
                <div className="col-md-12">
                  <h3 className="tb-mes-projet-titre mb-4">
                    Demandes de retrait
                  </h3>
                </div>

                <div className="row mb-4">
                  {[
                    { label: "En attente", valeur: totaux.attente, couleur: RETRAIT_STATUT.demande.color },
                    { label: "En cours de versement", valeur: totaux.encours, couleur: RETRAIT_STATUT.en_cours.color },
                    { label: "Versé", valeur: totaux.verse, couleur: RETRAIT_STATUT.verse.color },
                  ].map((c) => (
                    <div className="col-md-4 mb-2" key={c.label}>
                      <div
                        className="p-3"
                        style={{
                          background: "#fff",
                          borderLeft: `4px solid ${c.couleur}`,
                          boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                          {c.label}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>
                          {formatMontant(c.valeur)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-3">
                  <label className="me-2" htmlFor="filtre-statut">
                    Statut
                  </label>
                  <select
                    id="filtre-statut"
                    className="form-select d-inline-block"
                    style={{ width: 240 }}
                    value={statut}
                    onChange={(e) => setStatut(e.target.value)}
                  >
                    <option value="">Tous</option>
                    {Object.entries(RETRAIT_STATUT).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isLoading && <p>Chargement…</p>}
                {!isLoading && retraits.length === 0 && (
                  <p>Aucune demande de retrait.</p>
                )}

                {retraits.length > 0 && (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Projet</th>
                          <th>Demandé par</th>
                          <th>Montant</th>
                          <th>Reçu</th>
                          <th>Destination</th>
                          <th>Statut</th>
                          <th>Versement</th>
                          <th>Motif</th>
                          {isAdmin && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {retraits.map((r) => {
                          const st = RETRAIT_STATUT[r.statut];
                          return (
                            <tr key={r.slug}>
                              <td>{moment(r.created_at).format("DD/MM/YYYY")}</td>
                              <td>{nomProjet(r)}</td>
                              <td>{nomDemandeur(r)}</td>
                              <td>{formatMontant(r.montant)}</td>
                              <td>{formatMontant(r.montant_net)}</td>
                              <td style={{ fontSize: 12 }}>{destinationRetrait(r)}</td>
                              <td>
                                <span
                                  style={{ color: st?.color, fontWeight: 600 }}
                                >
                                  {st?.label || r.statut}
                                </span>
                              </td>
<td style={{ fontSize: 12 }}>
                                {(() => {
                                  const o = origineVersement(r);
                                  if (!o) return "—";
                                  return (
                                    <>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          color: o.auto ? "#0FBF56" : "#6c757d",
                                        }}
                                      >
                                        {o.label}
                                      </span>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: "rgba(0,0,0,0.5)",
                                        }}
                                      >
                                        {o.reference}
                                      </div>
                                    </>
                                  );
                                })()}
                              </td>
                              <td style={{ maxWidth: 200 }}>{r.motif || "—"}</td>
                              {isAdmin && (
                                <td>
                                  {r.mode === "carte" &&
                                    ["demande", "approuve", "en_cours", "echoue"].includes(
                                      r.statut
                                    ) && (
                                      <button
                                        type="button"
                                        disabled={lectureCarte}
                                        className="btn btn-sm me-1 mb-1"
                                        style={{
                                          border: `1px solid ${COLORS.blue}`,
                                          color: COLORS.blue,
                                          fontSize: 11,
                                        }}
                                        onClick={() => afficherCarte(r)}
                                      >
                                        Numéro de carte
                                      </button>
                                    )}
                                  {transitionsAdmin(r).map(
                                    (s) => (
                                      <button
                                        key={s}
                                        type="button"
                                        disabled={isUpdating}
                                        className="btn btn-sm me-1 mb-1"
                                        style={{
                                          border: `1px solid ${RETRAIT_STATUT[s].color}`,
                                          color: RETRAIT_STATUT[s].color,
                                          fontSize: 11,
                                        }}
                                        onClick={() => changer(r, s)}
                                      >
                                        {ACTIONS[s]}
                                      </button>
                                    )
                                  )}
                                  {transitionsAdmin(r)
                                    .length === 0 && "—"}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetraitsAdmin;
